import React from 'react';
import I18n from 'i18n-js';
import track from 'react-tracking';
import gql from 'graphql-tag';
import { withUserContext, IUserContext } from './user_context_provider';
import { isExperimentEnabled } from '../user_context_helpers';
import experiments from '../experiments';
import { userIsTrackable } from '../elog/mparticle';
import ListingsCollection, { ListingsCollection as ListingsCollectionClass } from './listings_collection';
import { mParticleListingFields } from './with_mparticle_listings';
import { CommonsWatchedListings } from '../rql';
import EmptyStateWatchedListings from './empty_state_watched_listings';
import { withGraphql } from '../with_graphql';
import { ChildProps } from 'react-apollo';
import { connect } from '../connect';

const COMPONENT_NAME = 'WatchRow';
const MAX_COUNT = 12;
const MIN_COUNT = 4;

export interface ExternalProps {
  mainCallToAction?: React.ReactElement;
  secondaryCallToAction?: React.ReactElement;
}

type IProps = ChildProps<IUserContext & ExternalProps, CommonsWatchedListings.Query>;

const withQuery = withGraphql<IProps, CommonsWatchedListings.Query, CommonsWatchedListings.Variables>(
  gql`
    query Commons_WatchedListings(
      $limit: Int
      $showListingLocation: Boolean!
      $shouldSkipTracking: Boolean!
    ) {
      me {
        uuid
        watches(input: {
          limit: $limit
          listingsState: LIVE
        }) {
          nodes {
            _id
            listing {
              _id
              ... ListingsCollection
              ... mParticleListingFields @skip(if: $shouldSkipTracking)
              ... ShopFields @include(if: $showListingLocation)
            }
          }
        }
      }
    }
    ${ListingsCollectionClass.fragments.listing}
    ${ListingsCollectionClass.fragments.shopFields}
    ${mParticleListingFields}
  `,
  {
    options: ownProps => ({
      ssr: true,
      skip: !!ownProps.user?.loggedOut,
      variables: {
        limit: MAX_COUNT,
        showListingLocation: isExperimentEnabled(ownProps.user, experiments.LISTING_CARD_LOCATION),
        shouldSkipTracking: !userIsTrackable(),
      },
    }),
  },
);

export function WatchedListings(props: IProps) {
  const { data: { me, loading } } = props;
  const listings = (me?.watches?.nodes ?? []).map(n => n.listing);
  const isEmptyWatchlist = !loading && !listings.length;

  if (isEmptyWatchlist) {
    return (
      <EmptyStateWatchedListings
        user={props.user}
        mainCallToAction={props.mainCallToAction}
        secondaryCallToAction={props.secondaryCallToAction}
      />
    );
  }

  if (listings.length < MIN_COUNT) { return null; }

  return (
    <ListingsCollection
      listings={listings}
      loading={loading}
      callToAction={props.mainCallToAction}
      componentName={COMPONENT_NAME}
      title={I18n.t('commons.watchRow.loggedIn.title')}
      largeTiles
    />
  );
}

const connected = connect<ExternalProps>([
  withUserContext,
  withQuery,
])(track({ componentName: COMPONENT_NAME })(WatchedListings));

export default connected;
